import React, { useEffect, useState, useRef } from "react";
import { dataStr } from "../utils/periodoLavorativo";
import { collection, onSnapshot, query, orderBy, updateDoc, doc, getDocs, where, addDoc, setDoc, serverTimestamp, deleteDoc, writeBatch } from "firebase/firestore";
import { getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { messaggioErrore } from "../utils/erroriUtente";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../utils/Toast";
import { Building2, Users, CreditCard, TrendingUp, CheckCircle, XCircle, Lock, Unlock, Plus, X, ArrowRight, LogIn, Clock, Trash2, AlertTriangle } from "lucide-react";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import { confirm } from "../utils/confirm";
import { logModifica } from "../utils/logModifica";
import { getFunctions, httpsCallable } from "firebase/functions";
import { dominioAziendaleValido } from "../utils/dominioAziendale";
import { pivaValida, codiceFiscaleValido } from "../utils/validazionePiva";
import { LISTINO, PIANI_INFO, postiDaPagare, canoneMensile, canoneAnnuale, normalizzaPiano, euro } from "../utils/pianiNexiora";
import DatiFornitoreModal from "../components/DatiFornitoreModal";
import FormazioneOwner from "../components/FormazioneOwner";
import AbbonamentiOwner from "../components/AbbonamentiOwner";
import AvvisiPagamenti from "../components/AvvisiPagamenti";

function giorniA(data) {
  if (!data) return null;
  const end = data?.toDate ? data.toDate() : new Date(data);
  const oggi = new Date(); oggi.setHours(0,0,0,0);
  return Math.round((end - oggi) / 86400000);
}

// Modal nuova organizzazione
function NewOrgModal({ onClose, currentUser, onEnterOrg }) {
  const { addToast } = useToast();
  const [form, setForm] = useState({ orgName:"", piva:"", indirizzo:"", adminName:"", adminEmail:"", phone:"" });
  const [loading, setLoading] = useState(false);
  const [useCurrentUser, setUseCurrentUser] = useState(true);

  const submit = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      // Stessa validazione formale del form pubblico — prima non c'era nessun controllo
      // qui, solo un campo facoltativo: un errore di battitura nella P.IVA di un cliente
      // vero passava inosservato. Riusa le stesse funzioni già verificate.
      const pivaPulita = form.piva.trim();
      if (pivaPulita) {
        if (pivaPulita.length !== 11 && pivaPulita.length !== 16) { addToast("Partita IVA non valida (11 cifre) o Codice Fiscale (16 caratteri)", "error"); setLoading(false); return; }
        if (pivaPulita.length === 11 && !pivaValida(pivaPulita)) { addToast("Partita IVA non valida — verifica di averla inserita correttamente", "error"); setLoading(false); return; }
        if (pivaPulita.length === 16 && !codiceFiscaleValido(pivaPulita)) { addToast("Codice Fiscale non valido — verifica di averlo inserito correttamente", "error"); setLoading(false); return; }
      }

      let adminUid, adminEmail, adminName;

      if (useCurrentUser) {
        // Usa account Paolo esistente
        adminUid = currentUser.uid;
        adminEmail = currentUser.email;
        adminName = currentUser.displayName;
      } else {
        // W447: l'account lo crea il server, SENZA password: il titolare riceve l'email «Imposta
        // la tua password» (dopo la creazione dell'azienda). La password non la sceglie né la
        // conosce nessun altro, e la tua sessione resta intatta.
        const { data } = await httpsCallable(getFunctions(), "creaAccountTitolare")({ email: form.adminEmail.trim(), nome: form.adminName.trim() });
        adminUid = data.uid;
        adminEmail = form.adminEmail.trim().toLowerCase();
        adminName = form.adminName.trim();
      }

      // Controllo duplicati P.IVA
      if (form.piva.trim()) {
        const existing = await getDocs(query(collection(db, "organizations"), where("piva", "==", form.piva.trim())));
        if (!existing.empty) { addToast("Questa Partita IVA è già registrata su Nexiora.", "error"); setLoading(false); return; }
      }

      // Da qui in poi, se qualcosa fallisce, ripuliamo quanto già creato — stesso principio
      // già applicato alla registrazione pubblica: meglio poter riprovare da capo che
      // restare con un account o un'organizzazione a metà.
      let orgRef = null;
      try {
        // Crea organizzazione — stesso trial di 90 giorni della registrazione normale, per
        // coerenza: anche le organizzazioni create da qui (es. per una demo/prova) partono
        // con lo stesso conteggio automatico, non serve più impostarlo a mano con Proroga.
        orgRef = await addDoc(collection(db, "organizations"), {
          name: form.orgName,
          // W446: la P.IVA si scrive subito dopo con impostaPivaOrganizzazione, che la mette
          // anche nel registro partiteIva (unicità garantita); se è già usata, l'azienda si toglie
          piva: "",
          indirizzo: form.indirizzo.trim(),
          ownerId: adminUid,
          plan: "free",
          status: "trialing",
          trialEndsAt: new Date(Date.now() + LISTINO.giorniProva*24*60*60*1000),
          seats: 1,
          seatsUsed: useCurrentUser ? 0 : 1,
          stripeCustomerId: null,
          stripeSubscriptionId: null,
          billingCycle: null,
          // W419: le aziende nuove nascono con «Timbrature dimenticate: compila o giustifica»
          obbligoPresenze: "normale", obbligoPresenzeDal: dataStr(new Date()),
          dominioAziendale: dominioAziendaleValido(adminEmail),
          formatoNumerazioneCommesse: "progressivo-anno", // di default, così la numerazione automatica funziona da subito, senza dover andare a impostarla
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });

        if (form.piva.trim()) {
          try { await httpsCallable(getFunctions(), "impostaPivaOrganizzazione")({ orgId: orgRef.id, piva: form.piva.trim() }); }
          catch (e) { throw new Error(e?.details?.codice === "duplicata" ? "Questa Partita IVA è già registrata su Nexiora." : (e?.message || "Partita IVA non salvata")); }
        }

        // Crea/aggiorna profilo utente amministratore.
        // IMPORTANTE: se si sta usando l'account già esistente (es. il Platform Owner
        // che crea un'organizzazione di prova), NON tocchiamo il suo organizationId —
        // farlo lo sposterebbe silenziosamente fuori dalla sua organizzazione reale.
        // Solo per un account NUOVO ha senso assegnare l'organizzazione appena creata.
        if (useCurrentUser) {
          addToast(`Organizzazione "${form.orgName}" creata — il tuo account resta collegato alla tua organizzazione abituale, non a questa nuova.`, "success");
        } else {
          // role: "titolare", non "admin" — questa persona è il fondatore/titolare vero
          // dell'organizzazione appena creata, non un amministratore aggiunto in seguito.
          // Prima era "admin": l'organizzazione nasceva senza nessun Titolare fin dal primo
          // giorno, esattamente lo scenario che il controllo "mai l'ultimo Titolare" altrove
          // nell'app è pensato per evitare — qui invece si partiva già da zero Titolari.
          await setDoc(doc(db, "users", adminUid), {
            uid: adminUid,
            email: adminEmail,
            displayName: adminName,
            phone: form.phone || "",
            role: "titolare",
            organizationId: orgRef.id,
            enabled: true,
            createdAt: serverTimestamp(),
            department: "",
            fcmToken: "",
          }, { merge: true });
          try {
            await httpsCallable(getFunctions(), "inviaEmailImpostaPassword")({ email: adminEmail });
            addToast(`Organizzazione "${form.orgName}" creata: ${adminEmail} ha ricevuto l'email «Imposta la tua password».`, "success", 9000);
          } catch {
            addToast(`Organizzazione "${form.orgName}" creata, ma l'email per la password non è partita: dalla pagina di accesso il titolare può usare «Password dimenticata?».`, "info", 12000);
          }
        }
      } catch (errInterno) {
        if (orgRef) { try { await deleteDoc(orgRef); } catch { /* l'errore resta comunque chiaro */ } }
        throw errInterno;
      }
      onClose();
      // Entra subito nell'organizzazione appena creata, SOLO se hai usato il tuo stesso
      // account come titolare — altrimenti resteresti "dentro" un'organizzazione a cui
      // il tuo profilo non appartiene davvero. Bug reale trovato oggi: prima si restava
      // sempre sulla Dashboard dopo la creazione, senza nessun avviso — capitava di
      // continuare a testare, senza accorgersene, sull'organizzazione precedente.
      if (useCurrentUser && orgRef) {
        onEnterOrg({ id: orgRef.id, name: form.orgName });
      }
    } catch(err) {
      addToast(messaggioErrore(err), "error");
    }
    setLoading(false);
  };

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000, padding:20 }}>
      <div style={{ background:"#1a1a2e", border:"1px solid rgba(255,255,255,0.1)", borderRadius:16, padding:28, width:"100%", maxWidth:480 }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:20 }}>
          <h3 style={{ fontSize:16, fontWeight:700, color:"#fff" }}>Nuova organizzazione</h3>
          <button onClick={onClose} style={{ background:"none", border:"none", cursor:"pointer", color:"rgba(255,255,255,0.5)", fontSize:20 }}>×</button>
        </div>

        <form onSubmit={submit}>
          {/* Nome organizzazione */}
          <div style={{ marginBottom:14 }}>
            <label style={{ display:"block", fontSize:11, fontWeight:600, color:"rgba(255,255,255,0.4)", textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:6 }}>Nome azienda</label>
            <input value={form.orgName} onChange={e => setForm(f => ({...f, orgName:e.target.value}))}
              placeholder="Es. Rossi Impianti Srl" required
              style={{ width:"100%", background:"rgba(255,255,255,0.08)", border:"1px solid rgba(255,255,255,0.12)", borderRadius:8, padding:"9px 12px", color:"#fff", fontSize:13, fontFamily:"'Inter',sans-serif", outline:"none", boxSizing:"border-box" }}/>
          </div>

          {/* P.IVA */}
          <div style={{ marginBottom:14 }}>
            <label style={{ display:"block", fontSize:11, fontWeight:600, color:"rgba(255,255,255,0.4)", textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:6 }}>Partita IVA</label>
            <input value={form.piva} onChange={e => setForm(f => ({...f, piva:e.target.value}))}
              placeholder="Es. 01234567890 (facoltativa)" maxLength={16}
              style={{ width:"100%", background:"rgba(255,255,255,0.08)", border:"1px solid rgba(255,255,255,0.12)", borderRadius:8, padding:"9px 12px", color:"#fff", fontSize:13, fontFamily:"'Inter',sans-serif", outline:"none", boxSizing:"border-box" }}/>
            <div style={{ fontSize:11, color:"rgba(255,255,255,0.3)", marginTop:4 }}>Nexiora verifica che non esista già un account con questa P.IVA</div>
          </div>

          {/* Indirizzo */}
          <div style={{ marginBottom:14 }}>
            <label style={{ display:"block", fontSize:11, fontWeight:600, color:"rgba(255,255,255,0.4)", textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:6 }}>Indirizzo sede *</label>
            <input value={form.indirizzo} onChange={e => setForm(f => ({...f, indirizzo:e.target.value}))}
              placeholder="Es. Via Roma 1, 80100 Napoli" required
              style={{ width:"100%", background:"rgba(255,255,255,0.08)", border:"1px solid rgba(255,255,255,0.12)", borderRadius:8, padding:"9px 12px", color:"#fff", fontSize:13, fontFamily:"'Inter',sans-serif", outline:"none", boxSizing:"border-box" }}/>
          </div>

          {/* Scelta admin */}
          <div style={{ marginBottom:14 }}>
            <label style={{ display:"block", fontSize:11, fontWeight:600, color:"rgba(255,255,255,0.4)", textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:8 }}>Amministratore</label>
            <div style={{ display:"flex", gap:8, marginBottom:12 }}>
              <button type="button" onClick={() => setUseCurrentUser(true)}
                style={{ flex:1, padding:"8px", border:`2px solid ${useCurrentUser?"#7c3aed":"rgba(255,255,255,0.12)"}`, borderRadius:8, background:useCurrentUser?"rgba(124,58,237,0.2)":"transparent", color:useCurrentUser?"#a78bfa":"rgba(255,255,255,0.5)", fontSize:12, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
                Il mio account (Paolo)
              </button>
              <button type="button" onClick={() => setUseCurrentUser(false)}
                style={{ flex:1, padding:"8px", border:`2px solid ${!useCurrentUser?"#7c3aed":"rgba(255,255,255,0.12)"}`, borderRadius:8, background:!useCurrentUser?"rgba(124,58,237,0.2)":"transparent", color:!useCurrentUser?"#a78bfa":"rgba(255,255,255,0.5)", fontSize:12, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
                Nuovo account
              </button>
            </div>

            {useCurrentUser ? (
              <div style={{ background:"rgba(124,58,237,0.1)", border:"1px solid rgba(124,58,237,0.3)", borderRadius:8, padding:"10px 14px", fontSize:13, color:"#a78bfa" }}>
                ✓ Userai il tuo account attuale come admin di questa organizzazione
              </div>
            ) : (
              <>
                <input value={form.adminName} onChange={e => setForm(f => ({...f, adminName:e.target.value}))}
                  placeholder="Nome e cognome admin" required={!useCurrentUser}
                  style={{ width:"100%", background:"rgba(255,255,255,0.08)", border:"1px solid rgba(255,255,255,0.12)", borderRadius:8, padding:"9px 12px", color:"#fff", fontSize:13, fontFamily:"'Inter',sans-serif", outline:"none", marginBottom:8, boxSizing:"border-box" }}/>
                <input value={form.adminEmail} onChange={e => setForm(f => ({...f, adminEmail:e.target.value}))}
                  placeholder="Email admin" type="email" required={!useCurrentUser}
                  style={{ width:"100%", background:"rgba(255,255,255,0.08)", border:"1px solid rgba(255,255,255,0.12)", borderRadius:8, padding:"9px 12px", color:"#fff", fontSize:13, fontFamily:"'Inter',sans-serif", outline:"none", marginBottom:8, boxSizing:"border-box" }}/>
<div style={{ fontSize:12, color:"rgba(255,255,255,0.55)", lineHeight:1.5 }}>Nessuna password da scegliere: il titolare riceve un'email Nexiora con il pulsante «Imposta la tua password».</div>
              </>
            )}
          </div>

          <div style={{ display:"flex", gap:8, justifyContent:"flex-end", marginTop:20 }}>
            <button type="button" onClick={onClose}
              style={{ padding:"8px 16px", background:"rgba(255,255,255,0.08)", border:"1px solid rgba(255,255,255,0.12)", borderRadius:8, color:"rgba(255,255,255,0.7)", fontSize:13, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
              Annulla
            </button>
            <button type="submit" disabled={loading}
              style={{ padding:"8px 20px", background:"#7c3aed", border:"none", borderRadius:8, color:"#fff", fontSize:13, fontWeight:600, cursor:"pointer", fontFamily:"'Inter',sans-serif", opacity:loading?0.7:1 }}>
              {loading ? "Creo..." : "Crea organizzazione"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Avvisi per chi gestisce Nexiora (ott 2026): nuove registrazioni dall'ultima visita e
// indirizzi aggiuntivi per le email (oltre all'email commerciale del fornitore).
// Dati in piattaformaPrivata/notifiche — leggibili e modificabili solo dal Platform Owner.
function AvvisiOwner({ orgs }) {
  const { addToast } = useToast();
  const [dati, setDati] = useState(null);
  const [testo, setTesto] = useState("");
  const [aperto, setAperto] = useState(false);
  useEffect(() => {
    getDoc(doc(db, "piattaformaPrivata", "notifiche")).then(s => {
      const d = s.exists() ? s.data() : {}; setDati(d); setTesto((d.emailAggiuntive || []).join("\n"));
    }).catch(() => setDati({}));
  }, []);
  if (!dati) return null;
  const ultimo = dati.ultimaVisitaNuove?.toDate ? dati.ultimaVisitaNuove.toDate() : null;
  const daRicontrollare = orgs.filter(o => o.viesDaRicontrollare === true);
  const nuove = orgs.filter(o => o.id !== "demo-nexiora-org" && o.createdAt?.toDate && (!ultimo || o.createdAt.toDate() > ultimo));
  const salva = async (campi) => {
    try { await setDoc(doc(db, "piattaformaPrivata", "notifiche"), { ...campi, aggiornatoIl: serverTimestamp() }, { merge: true }); setDati(d => ({ ...d, ...campi, ...(campi.ultimaVisitaNuove ? { ultimaVisitaNuove: { toDate: () => new Date() } } : {}) })); return true; }
    catch (e) { addToast(messaggioErrore(e), "error"); return false; }
  };
  const box = { margin: window.innerWidth < 768 ? "12px 12px 0" : "16px 32px 0", padding: "12px 16px", borderRadius: 12, border: "1px solid rgba(167,139,250,.35)", background: "rgba(124,58,237,.12)", fontSize: 13 };
  return (
    <div style={box}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <strong style={{ color: "#ddd6fe" }}>🔔 {nuove.length ? `${nuove.length} ${nuove.length === 1 ? "nuova registrazione" : "nuove registrazioni"} dall'ultima visita` : "Nessuna nuova registrazione dall'ultima visita"}</strong>
        {nuove.length > 0 && <span style={{ color: "#c4b5fd" }}>{nuove.slice(0, 5).map(o => o.name || o.id).join(" · ")}</span>}
        <div style={{ flex: 1 }} />
        {nuove.length > 0 && <button onClick={() => salva({ ultimaVisitaNuove: serverTimestamp() })} style={{ background: "transparent", border: "1px solid rgba(196,181,253,.5)", color: "#ddd6fe", borderRadius: 8, padding: "4px 10px", cursor: "pointer", fontSize: 12 }}>Segna come viste</button>}
        <button onClick={() => setAperto(!aperto)} style={{ background: "transparent", border: "none", color: "#c4b5fd", cursor: "pointer", fontSize: 12, textDecoration: "underline" }}>Indirizzi per gli avvisi e registro P.IVA</button>
      </div>
      {daRicontrollare.length > 0 && (
        <div data-test="piva-da-ricontrollare" style={{ marginTop: 8, color: "#fde68a" }}>
          🧾 Partite IVA non confermate dal VIES (non iscritte alle operazioni con l'estero, o VIES fuori servizio al momento della registrazione): controllale con la visura o sul sito dell'Agenzia delle Entrate.
          {daRicontrollare.map(o => (
            <div key={o.id} style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 4, color: "#fef3c7" }}>
              <span>{o.name || o.id} · P.IVA {o.piva}</span>
              <button onClick={async () => { try { await updateDoc(doc(db, "organizations", o.id), { viesDaRicontrollare: false, pivaControllataIl: serverTimestamp() }); addToast("Segnata come controllata", "success"); } catch (e) { addToast(messaggioErrore(e), "error"); } }}
                style={{ background: "transparent", border: "1px solid rgba(253,230,138,.5)", color: "#fde68a", borderRadius: 8, padding: "2px 8px", cursor: "pointer", fontSize: 11 }}>Controllata</button>
            </div>
          ))}
        </div>
      )}
      {aperto && (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 12, color: "#c4b5fd", marginBottom: 6 }}>Registro delle partite IVA (W446): garantisce che ogni P.IVA sia usata da una sola azienda. Da allineare una volta dopo l'aggiornamento (le aziende nuove ci entrano da sole).</div>
          <button onClick={async () => {
            try {
              const { data } = await httpsCallable(getFunctions(), "migraRegistroPartiteIva")({});
              addToast(`Registro allineato: ${data.creati} aggiunte, ${data.giaPresenti} già presenti${data.doppie.length ? ` · ${data.doppie.length} P.IVA usate da più aziende da sistemare: ${data.doppie.map(x => x.piva).join(", ")}` : ""}`, data.doppie.length ? "info" : "success", 12000);
            } catch (e) { addToast(messaggioErrore(e), "error"); }
          }} style={{ marginBottom: 12, background: "#7c3aed", color: "#fff", border: "none", borderRadius: 8, padding: "6px 14px", cursor: "pointer", fontWeight: 700, fontSize: 12 }}>Allinea il registro P.IVA</button>
          <div style={{ fontSize: 12, color: "#c4b5fd", marginBottom: 6 }}>Gli avvisi (nuove registrazioni, prove in scadenza o scadute, riepilogo del lunedì) arrivano all'email commerciale dei dati del fornitore. Qui puoi aggiungere altri indirizzi, uno per riga.</div>
          <textarea value={testo} onChange={e => setTesto(e.target.value)} rows={3} style={{ width: "100%", boxSizing: "border-box", borderRadius: 8, border: "1px solid rgba(255,255,255,.2)", background: "rgba(0,0,0,.3)", color: "#fff", padding: 8, fontFamily: "inherit", fontSize: 13 }} />
          <button onClick={async () => {
            const lista = testo.split(/[\s,;]+/).map(x => x.trim()).filter(Boolean);
            const errati = lista.filter(x => !/^\S+@\S+\.\S+$/.test(x));
            if (errati.length) { addToast(`Indirizzi non validi: ${errati.join(", ")}`, "error"); return; }
            if (await salva({ emailAggiuntive: lista })) addToast("Indirizzi salvati", "success");
          }} style={{ marginTop: 8, background: "#7c3aed", color: "#fff", border: "none", borderRadius: 8, padding: "6px 14px", cursor: "pointer", fontWeight: 700, fontSize: 12 }}>Salva</button>
        </div>
      )}
    </div>
  );
}

export default function OwnerDashboard({ onEnterOrg }) {
  const { currentUser } = useAuth();
  const { addToast } = useToast();
  const [orgs, setOrgs] = useState([]);
  const [stats, setStats] = useState({ totalOrgs:0, totalUsers:0, paidOrgs:0, revenue:0 });
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [showEliminaOrg, setShowEliminaOrg] = useState(false);
  const [allineando, setAllineando] = useState(false);
  const [esitoAllineamento, setEsitoAllineamento] = useState(null);
  const [confermaNomeOrg, setConfermaNomeOrg] = useState("");
  const [eliminandoOrg, setEliminandoOrg] = useState(false);
  const [orgUsers, setOrgUsers] = useState([]);
  const [showNewOrg, setShowNewOrg] = useState(false);
  const [showFornitore, setShowFornitore] = useState(false);
  const [showFormazione, setShowFormazione] = useState(false); // W427: calendario della formazione
  const [showAbbonamenti, setShowAbbonamenti] = useState(false); // W431: richieste, proforma, piani attivi
  // W433: link al Manuale interno del pannello Owner (file su Google Drive condiviso solo con chi
  // deve leggerlo; il link sta in piattaformaPrivata/manuale, leggibile solo dal Platform Owner)
  const [linkManuale, setLinkManuale] = useState("");
  useEffect(() => onSnapshot(doc(db, "piattaformaPrivata", "manuale"), s => setLinkManuale((s.exists() && s.data().link) || ""), () => {}), []);
  const impostaManuale = async () => {
    const v = window.prompt("Incolla il link del Manuale interno su Google Drive (condiviso solo con chi deve leggerlo):", linkManuale || "https://drive.google.com/");
    if (v === null) return;
    const link = v.trim();
    if (link && !/^https:\/\/\S+$/.test(link)) { addToast("Il link deve iniziare con https://", "error"); return; }
    try { await setDoc(doc(db, "piattaformaPrivata", "manuale"), { link, aggiornatoIl: serverTimestamp() }, { merge: true }); addToast(link ? "Link del manuale salvato" : "Link del manuale tolto", "success"); }
    catch (e) { addToast("Non salvato: " + (e?.message || ""), "error"); }
  };
  const [giorniProroga, setGiorniProroga] = useState("");
  const [applicandoProroga, setApplicandoProroga] = useState(false);
  // Telefono (ottobre 2026): sotto i 768 px la pagina si impila e il dettaglio va sotto l'elenco
  const [stretto, setStretto] = useState(() => typeof window !== "undefined" && window.innerWidth < 768);
  useEffect(() => {
    const h = () => setStretto(window.innerWidth < 768);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  const refDettaglio = useRef(null);

  // Eliminazione organizzazione — irreversibile, solo Platform Owner (già garantito da chi
  // può raggiungere questa pagina, e ricontrollato lato server dentro la Cloud Function
  // stessa). Richiede di scrivere il nome esatto dell'organizzazione prima di procedere.
  // Allineamento una tantum — scrive statoCommessa:"attiva" sulle commesse che non hanno il
  // campo. Serve perché le pagine filtrano ora lato server su questo campo (per non scaricare
  // l'intero storico a ogni apertura): un documento senza il campo non verrebbe restituito da
  // nessuna di quelle query, e sparirebbe dalle tendine.
  // Non tocca nulla di ciò che è già valorizzato: le commesse chiuse restano chiuse.
  const allineaStatoCommesse = async () => {
    if (allineando) return;
    setAllineando(true);
    setEsitoAllineamento(null);
    try {
      const snap = await getDocs(collection(db, "projects"));
      const daSistemare = snap.docs.filter(d => {
        const p = d.data();
        return !p.isParent && (p.statoCommessa === undefined || p.statoCommessa === null || p.statoCommessa === "");
      });
      if (daSistemare.length === 0) {
        setEsitoAllineamento({ ok:true, testo:`Nessuna commessa da allineare — tutte già a posto (${snap.size} documenti controllati).` });
        setAllineando(false);
        return;
      }
      // Firestore accetta al massimo 500 operazioni per lotto: 400 lascia margine di sicurezza.
      for (let i = 0; i < daSistemare.length; i += 400) {
        const lotto = writeBatch(db);
        daSistemare.slice(i, i + 400).forEach(d => lotto.update(d.ref, { statoCommessa: "attiva" }));
        await lotto.commit();
      }
      setEsitoAllineamento({ ok:true, testo:`${daSistemare.length} commesse allineate su ${snap.size} documenti controllati.` });
      addToast("Allineamento completato ✅","success");
    } catch (err) {
      console.error("Errore allineamento stato commesse:", err);
      setEsitoAllineamento({ ok:false, testo: messaggioErrore(err) });
    } finally {
      setAllineando(false);
    }
  };

  const eliminaOrganizzazione = async () => {
    if (!selectedOrg) return;
    if (confermaNomeOrg.trim() !== (selectedOrg.name || "").trim()) {
      addToast("Il nome digitato non corrisponde — controlla di averlo scritto esattamente uguale", "error");
      return;
    }
    setEliminandoOrg(true);
    try {
      const fn = httpsCallable(getFunctions(), "eliminaOrganizzazione");
      const res = await fn({ orgId: selectedOrg.id, nomeConferma: confermaNomeOrg.trim() });
      addToast(`"${res.data.nome}" eliminata — ${res.data.utentiEliminati} profilo/i utente rimosso/i (gli account di accesso restano su Firebase Authentication, da rimuovere manualmente se necessario)`, "success");
      setShowEliminaOrg(false);
      setConfermaNomeOrg("");
      setSelectedOrg(null);
    } catch (err) {
      addToast("Errore nell'eliminazione: " + (err?.message || "errore sconosciuto"), "error");
    }
    setEliminandoOrg(false);
  };

  useEffect(() => {
    const q = query(collection(db, "organizations"), orderBy("createdAt", "desc"));
    return onSnapshot(q, snap => {
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setOrgs(list);
      let totalUsers = 0, paidOrgs = 0, revenue = 0;
      list.forEach(org => {
        totalUsers += org.seatsUsed || 0;
        if (org.plan && org.plan !== "free") {
          paidOrgs++;
          // Canone dal listino (utils/pianiNexiora.js), solo per piani attivi.
          if (org.status === "active") {
            const posti = org.seats || postiDaPagare(org.seatsUsed || 0);
            revenue += org.billingCycle === "annual" ? canoneAnnuale(posti, org.plan) / 12 : canoneMensile(posti, org.plan);
          }
        }
      });
      setStats({ totalOrgs: list.length, totalUsers, paidOrgs, revenue });
    });
  }, []);

  const loadOrgUsers = async org => {
    setSelectedOrg(org);
    // Sul telefono il dettaglio compare sotto l'elenco: ci si porta lì
    if (stretto) setTimeout(() => refDettaglio.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
    const snap = await getDocs(query(collection(db, "users"), where("organizationId", "==", org.id)));
    setOrgUsers(snap.docs.map(d => d.data()));
  };

  // "Sospesa" è l'unico stato che blocca davvero il login (verificato in AuthContext.js) —
  // per questo va distinto con precisione da "In prova" (trialing) e "Attiva" (piano pagante),
  // che invece permettono sempre l'accesso e differiscono solo per le funzionalità disponibili.
  const toggleStatus = async org => {
    if (org.status === "suspended") {
      // Riattiva: torna allo stato corretto in base alla situazione reale, non sempre "active"
      // — altrimenti un'organizzazione ancora in prova verrebbe etichettata come se avesse
      // già un piano pagante, pur non avendone uno.
      const gTrial = giorniA(org.trialEndsAt);
      const nuovoStato = (org.plan && org.plan !== "free") ? "active" : (gTrial!==null && gTrial>0 ? "trialing" : "trialing");
      await updateDoc(doc(db, "organizations", org.id), { status: nuovoStato });
      addToast("Organizzazione riattivata", "success");
    } else {
      await updateDoc(doc(db, "organizations", org.id), { status: "suspended" });
      addToast("Organizzazione sospesa", "info");
    }
  };

  function statoVisualizzato(org) {
    if (org.status === "suspended") return { testo:"✗ Sospesa", colore:"#dc2626" };
    if (org.status === "trialing") return { testo:"🕐 In prova", colore:"#a78bfa" };
    if (org.status === "active") return { testo:"✓ Attiva", colore:"#059669" };
    return { testo:org.status||"—", colore:"#6b7280" };
  }

  // Un piano pagato vale solo con status "active" (vedi pianiNexiora.statoPiano): prima
  // si cambiava solo "plan" e l'organizzazione restava "trialing", quindi il piano scelto
  // non aveva effetto. I posti si portano almeno al blocco che copre gli utenti attivi.
  const setPlan = async (org, plan) => {
    const aggiornamento = plan === "free"
      ? { plan: "free", status: "trialing", updatedAt: serverTimestamp() }
      : { plan, status: "active", seats: Math.max(org.seats || 0, postiDaPagare(org.seatsUsed || 0)), updatedAt: serverTimestamp() };
    await updateDoc(doc(db, "organizations", org.id), aggiornamento);
    if (plan === "free") addToast("Piano tolto: l'organizzazione torna senza piano (sola lettura se la prova è finita)", "success");
    else addToast(`Piano ${PIANI_INFO[plan].label} attivo: ${aggiornamento.seats} posti, ${euro(canoneMensile(aggiornamento.seats, plan))}/mese IVA esclusa`, "success");
  };

  // Proroga trial — es. per un'azienda amica che chiede più tempo. Se il trial è ancora
  // attivo, la proroga si somma alla scadenza esistente; se è già scaduto, riparte da oggi.
  // Ogni proroga finisce nell'audit trail (Centro Operativo → categoria "Piattaforma").
  // Termina subito la prova (per verificare il comportamento a fine prova su
  // un'organizzazione di test, o per chiudere una prova su richiesta). Reversibile con
  // "Proroga". Finisce nel registro modifiche come la proroga.
  const terminaProvaOra = async (org) => {
    const ok = await confirm(`Terminare ADESSO la prova di "${org.name}"? Senza piano attivo passerà al Free (o in sola lettura se ha più di 3 utenti attivi). Si annulla con "Proroga".`, { type:"warning" });
    if (!ok) return;
    try {
      await updateDoc(doc(db,"organizations",org.id), { trialEndsAt: new Date(Date.now() - 60*1000), updatedAt: serverTimestamp() });
      await logModifica({
        orgId: org.id, uid: currentUser?.uid, nomeUtente: currentUser?.displayName||"Platform Owner",
        sezione: "piattaforma", refId: org.id, refNome: org.name,
        campo: "trialEndsAt", campoLabel: "Fine prova anticipata (Platform Owner)", before: "", after: new Date().toLocaleString("it-IT"),
      });
      addToast("Prova terminata", "success");
    } catch (e) { addToast("Operazione non riuscita: " + (e.message||""), "error"); }
  };

  const applicaProroga = async (org) => {
    const giorni = parseInt(giorniProroga, 10);
    if (!giorni || giorni <= 0) { addToast("Indica un numero di giorni valido", "error"); return; }
    const gAttuali = giorniA(org.trialEndsAt);
    const baseData = (gAttuali !== null && gAttuali > 0) ? (org.trialEndsAt?.toDate ? org.trialEndsAt.toDate() : new Date(org.trialEndsAt)) : new Date();
    const nuovaData = new Date(baseData.getTime() + giorni*24*60*60*1000);
    const confermato = await confirm(
      `Prorogare il trial di "${org.name}" di ${giorni} giorni? Nuova scadenza: ${nuovaData.toLocaleDateString("it-IT")}.`,
      { type:"warning" }
    );
    if (!confermato) return;
    setApplicandoProroga(true);
    try {
      await updateDoc(doc(db,"organizations",org.id), { trialEndsAt: nuovaData, status:"trialing", updatedAt: serverTimestamp() });
      await logModifica({
        orgId: org.id, uid: currentUser?.uid, nomeUtente: currentUser?.displayName||"Platform Owner",
        sezione: "piattaforma", refId: org.id, refNome: org.name,
        campo: "trialEndsAt", campoLabel: "Proroga trial (Platform Owner)",
        before: gAttuali!==null ? `scadenza tra ${gAttuali}g` : "nessun trial", after: `+${giorni}g → ${nuovaData.toLocaleDateString("it-IT")}`,
      });
      addToast(`Trial di "${org.name}" prorogato di ${giorni} giorni ✅`, "success");
      setGiorniProroga("");
      setSelectedOrg(o => o ? {...o, trialEndsAt: nuovaData, status:"trialing"} : o);
    } catch(err) { addToast("Errore durante la proroga — riprova", "error"); }
    setApplicandoProroga(false);
  };

  // Verifica se Paolo è admin di questa org
  const canEnterOrg = org => orgUsers.some(u => u.uid === currentUser.uid);

  const planColor = { free:"#6b7280", gold:"#d4af37", pro:"#7c3aed", enterprise:"#f59e0b" };

  return (
    // Scorrimento proprio della pagina: il body dell'app è bloccato (overflow hidden), quindi
    // senza questo sul telefono non si scendeva oltre le prime organizzazioni
    <div style={{ height:"100vh", overflowY:"auto", overflowX:"hidden", WebkitOverflowScrolling:"touch", background:"#0a0a14", color:"#fff", fontFamily:"'Inter',sans-serif" }}>
      {/* Header */}
      <AvvisiOwner orgs={orgs} />
      {/* W432: pagamenti da controllare (sempre visibile finché c'è qualcosa aperto) */}
      <AvvisiPagamenti orgs={orgs} onApriAbbonamenti={() => setShowAbbonamenti(true)} onApriFormazione={() => setShowFormazione(true)} />
      <div style={{ borderBottom:"1px solid rgba(255,255,255,0.08)", padding: stretto ? "12px 16px" : "14px 32px", display:"flex", alignItems:"center", gap:12, flexWrap:"wrap" }}>
        <svg width="32" height="32" viewBox="0 0 72 72" xmlns="http://www.w3.org/2000/svg">
          <defs><linearGradient id="odlg" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#a78bfa"/><stop offset="100%" stopColor="#7c3aed"/></linearGradient></defs>
          <polygon points="36,2 70,36 36,70 2,36" fill="url(#odlg)"/>
          <line x1="22" y1="24" x2="22" y2="48" stroke="white" strokeWidth="5" strokeLinecap="round"/>
          <line x1="50" y1="24" x2="50" y2="48" stroke="white" strokeWidth="5" strokeLinecap="round"/>
          <line x1="22" y1="24" x2="50" y2="48" stroke="white" strokeWidth="4.5" strokeLinecap="round"/>
        </svg>
        <span style={{ fontWeight:700, fontSize:17 }}>Nexiora</span>
        <span style={{ background:"rgba(124,58,237,0.3)", color:"#a78bfa", padding:"2px 10px", borderRadius:20, fontSize:11, fontWeight:600 }}>PLATFORM OWNER</span>
        <div style={{ flex:1 }}/>
        {!stretto && <span style={{ fontSize:12, color:"rgba(255,255,255,0.4)" }}>{format(new Date(), "EEEE d MMMM yyyy", { locale: it })}</span>}
      </div>

      <div style={{ padding: stretto ? 14 : 32 }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom: stretto ? 18 : 28, gap:12, flexWrap:"wrap" }}>
          <div>
            <h1 style={{ fontSize:22, fontWeight:800, marginBottom:4 }}>Dashboard Nexiora</h1>
            <p style={{ color:"rgba(255,255,255,0.4)", fontSize:13 }}>Panoramica di tutte le organizzazioni clienti</p>
          </div>
          <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
          <div style={{ display:"flex" }}>
            <button onClick={() => linkManuale ? window.open(linkManuale, "_blank", "noopener") : impostaManuale()} title={linkManuale ? "Apri il Manuale interno del pannello Owner" : "Imposta il link del Manuale interno"}
              style={{ display:"flex", alignItems:"center", gap:8, padding:"10px 14px", background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.15)", borderRadius:"10px 0 0 10px", color:"#fff", fontSize:13, fontWeight:600, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
              📘 Manuale Owner
            </button>
            <button onClick={impostaManuale} aria-label="Cambia il link del manuale" title="Cambia il link del manuale"
              style={{ padding:"10px 10px", background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.15)", borderLeft:"none", borderRadius:"0 10px 10px 0", color:"rgba(255,255,255,.7)", fontSize:13, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>✎</button>
          </div>
          <button onClick={() => setShowFornitore(true)}
            style={{ display:"flex", alignItems:"center", gap:8, padding:"10px 16px", background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.15)", borderRadius:10, color:"#fff", fontSize:13, fontWeight:600, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
            <Building2 size={15}/> Dati del fornitore
          </button>
          <button onClick={() => setShowFormazione(true)}
            style={{ display:"flex", alignItems:"center", gap:8, padding:"10px 16px", background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.15)", borderRadius:10, color:"#fff", fontSize:13, fontWeight:600, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
            🎓 Formazione
          </button>
          <button onClick={() => setShowAbbonamenti(true)}
            style={{ display:"flex", alignItems:"center", gap:8, padding:"10px 16px", background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.15)", borderRadius:10, color:"#fff", fontSize:13, fontWeight:600, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
            💶 Abbonamenti
          </button>
          <button onClick={() => setShowNewOrg(true)}
            style={{ display:"flex", alignItems:"center", gap:8, padding:"10px 20px", background:"#7c3aed", border:"none", borderRadius:10, color:"#fff", fontSize:13, fontWeight:600, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
            <Plus size={15}/> Nuova organizzazione
          </button>
          </div>
        </div>
        {showFornitore && <DatiFornitoreModal onClose={() => setShowFornitore(false)} addToast={addToast}/>}
        {showFormazione && <FormazioneOwner onClose={() => setShowFormazione(false)}/>}
        {showAbbonamenti && <AbbonamentiOwner onClose={() => setShowAbbonamenti(false)}/>}

        {/* Stats */}
        <div style={{ display:"grid", gridTemplateColumns: stretto ? "repeat(2,minmax(0,1fr))" : "repeat(4,1fr)", gap: stretto ? 10 : 14, marginBottom: stretto ? 18 : 28 }}>
          {[
            { label:"Organizzazioni", value:stats.totalOrgs, icon:Building2, color:"#7c3aed" },
            { label:"Utenti totali", value:stats.totalUsers, icon:Users, color:"#2563eb" },
            { label:"Piani pagati", value:stats.paidOrgs, icon:CreditCard, color:"#059669" },
            { label:"Revenue (€/mese)", value:euro(stats.revenue), icon:TrendingUp, color:"#d97706" },
          ].map(s => (
            <div key={s.label} style={{ background:"rgba(255,255,255,0.05)", border:"1px solid rgba(255,255,255,0.08)", borderRadius:12, padding: stretto ? 14 : 20, minWidth:0 }}>
              <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
                <s.icon size={15} color={s.color}/>
                <span style={{ fontSize:11, color:"rgba(255,255,255,0.4)", textTransform:"uppercase", letterSpacing:"0.04em" }}>{s.label}</span>
              </div>
              <div style={{ fontSize:26, fontWeight:800, color:s.color }}>{s.value}</div>
            </div>
          ))}
        </div>

        <div style={{ display:"grid", gridTemplateColumns: selectedOrg && !stretto ? "minmax(0,1fr) 360px" : "minmax(0,1fr)", gap:20 }}>
          {/* Lista organizzazioni */}
          <div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.08)", borderRadius:12, overflow:"hidden" }}>
            <div style={{ padding:"14px 20px", borderBottom:"1px solid rgba(255,255,255,0.08)", fontSize:14, fontWeight:600 }}>
              Organizzazioni ({orgs.length})
            </div>
            {orgs.length === 0 ? (
              <div style={{ padding:48, textAlign:"center", color:"rgba(255,255,255,0.3)" }}>
                <Building2 size={32} style={{ margin:"0 auto 12px", display:"block", opacity:0.3 }}/>
                <p style={{ fontSize:14, marginBottom:8 }}>Nessuna organizzazione ancora</p>
                <button onClick={() => setShowNewOrg(true)}
                  style={{ background:"#7c3aed", border:"none", borderRadius:8, padding:"8px 16px", color:"#fff", fontSize:13, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
                  Crea la prima
                </button>
              </div>
            ) : orgs.map(org => (
              <div key={org.id}
                style={{ display:"flex", alignItems:"center", gap: stretto ? 10 : 14, flexWrap: stretto ? "wrap" : "nowrap", padding: stretto ? "12px 14px" : "14px 20px", borderBottom:"1px solid rgba(255,255,255,0.05)", cursor:"pointer", background: selectedOrg?.id === org.id ? "rgba(124,58,237,0.1)" : "transparent", transition:"background 0.15s" }}
                onClick={() => loadOrgUsers(org)}>
                <div style={{ width:40, height:40, background:"rgba(124,58,237,0.2)", borderRadius:10, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                  <Building2 size={18} color="#a78bfa"/>
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontWeight:600, fontSize:14, marginBottom:2 }}>{org.name}</div>
                  <div style={{ fontSize:12, color:"rgba(255,255,255,0.4)", display:"flex", gap:10, flexWrap:"wrap" }}>
                    <span>{org.seatsUsed || 0} utenti</span>
                    <span>·</span>
                    <span style={{ color: statoVisualizzato(org).colore }}>
                      {statoVisualizzato(org).testo}
                    </span>
                    {org.createdAt && (
                      <span>· {format(org.createdAt.toDate?.() ?? new Date(), "d MMM yyyy", { locale: it })}</span>
                    )}
                  </div>
                </div>
                <div style={{ display:"flex", alignItems:"center", gap:8, flexShrink:0, flexWrap:"wrap", ...(stretto ? { width:"100%", paddingLeft:50 } : {}) }}>
                  <span style={{ fontSize:11, padding:"3px 10px", borderRadius:20, background: (planColor[org.plan]||planColor.free) + "33", color: planColor[org.plan]||planColor.free, fontWeight:600 }}>
                    {org.plan && org.plan !== "free" ? org.plan.charAt(0).toUpperCase()+org.plan.slice(1) : "Nessun piano"}
                  </span>
                  {/* Pulsante entra */}
                  <button onClick={e => { e.stopPropagation(); onEnterOrg(org); }}
                    style={{ display:"flex", alignItems:"center", gap:5, padding:"5px 12px", background:"rgba(124,58,237,0.3)", border:"1px solid rgba(124,58,237,0.5)", borderRadius:8, color:"#a78bfa", fontSize:12, cursor:"pointer", fontFamily:"'Inter',sans-serif", fontWeight:600 }}
                    title="Entra nell'app come utente di questa organizzazione">
                    <LogIn size={12}/> Entra
                  </button>
                  <button onClick={e => { e.stopPropagation(); toggleStatus(org); }}
                    style={{ background:"none", border:`1px solid rgba(255,255,255,0.1)`, borderRadius:6, padding:"4px 10px", cursor:"pointer", color: org.status === "suspended" ? "#059669" : "#dc2626", fontSize:11, fontFamily:"'Inter',sans-serif", display:"flex", alignItems:"center", gap:4 }}>
                    {org.status === "suspended" ? <><Unlock size={10}/> Riattiva</> : <><Lock size={10}/> Sospendi</>}
                  </button>
                  <select value={normalizzaPiano(org.plan)}
                    onChange={e => { e.stopPropagation(); setPlan(org, e.target.value); }}
                    onClick={e => e.stopPropagation()}
                    style={{ background:"rgba(255,255,255,0.08)", border:"1px solid rgba(255,255,255,0.12)", borderRadius:6, padding:"4px 8px", color:"#fff", fontSize:11, fontFamily:"'Inter',sans-serif", cursor:"pointer" }}>
                    <option value="free">Nessun piano</option>
                    <option value="pro">Pro</option>
                    <option value="gold">Gold</option>
                  </select>
                </div>
              </div>
            ))}
          </div>

          {/* Dettaglio organizzazione */}
          {selectedOrg && (
            <div ref={refDettaglio} style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.08)", borderRadius:12, overflow:"hidden", scrollMarginTop:12 }}>
              <div style={{ padding:"14px 20px", borderBottom:"1px solid rgba(255,255,255,0.08)", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                <div>
                  <div style={{ fontWeight:700, fontSize:15 }}>{selectedOrg.name}</div>
                  <div style={{ fontSize:11, color:"rgba(255,255,255,0.3)", marginTop:2 }}>ID: {selectedOrg.id}</div>
                </div>
                <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                  <button onClick={() => onEnterOrg(selectedOrg)}
                    style={{ display:"flex", alignItems:"center", gap:6, padding:"7px 14px", background:"#7c3aed", border:"none", borderRadius:8, color:"#fff", fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
                    <LogIn size={13}/> Entra nell'app
                  </button>
                  <button onClick={() => { setShowEliminaOrg(true); setConfermaNomeOrg(""); }}
                    title="Elimina organizzazione e tutti i suoi dati — irreversibile"
                    style={{ display:"flex", alignItems:"center", gap:6, padding:"7px 12px", background:"rgba(220,38,38,0.1)", border:"1px solid rgba(220,38,38,0.3)", borderRadius:8, color:"#f87171", fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
                    <Trash2 size={13}/> Elimina
                  </button>
                  <button onClick={() => setSelectedOrg(null)}
                    style={{ background:"none", border:"none", cursor:"pointer", color:"rgba(255,255,255,0.4)", fontSize:20 }}>×</button>
                </div>
              </div>

              {/* Info */}
              <div style={{ padding:"14px 20px", borderBottom:"1px solid rgba(255,255,255,0.05)" }}>
                {[
                  { label:"Piano", value: selectedOrg.plan && selectedOrg.plan !== "free" ? `${selectedOrg.plan.charAt(0).toUpperCase()+selectedOrg.plan.slice(1)} ✓` : "Nessun piano" },
                  { label:"Stato", value: statoVisualizzato(selectedOrg).testo },
                  { label:"Utenti attivi", value: `${selectedOrg.seatsUsed || 0}` },
                  { label:"Stripe", value: selectedOrg.stripeCustomerId || "Non configurato" },
                ].map(item => (
                  <div key={item.label} style={{ display:"flex", justifyContent:"space-between", padding:"6px 0", fontSize:13, borderBottom:"1px solid rgba(255,255,255,0.04)" }}>
                    <span style={{ color:"rgba(255,255,255,0.4)" }}>{item.label}</span>
                    <span style={{ fontWeight:500 }}>{item.value}</span>
                  </div>
                ))}
              </div>

              {/* Trial e proroga — separato dal blocco Info sopra perché richiede un
                  controllo interattivo, non solo un valore da mostrare. */}
              <div style={{ padding:"14px 20px", borderBottom:"1px solid rgba(255,255,255,0.05)" }}>
                <div style={{ fontSize:11, fontWeight:600, color:"rgba(255,255,255,0.4)", textTransform:"uppercase", letterSpacing:"0.04em", marginBottom:10, display:"flex", alignItems:"center", gap:6 }}>
                  <Clock size={12}/> Trial
                </div>
                {(() => {
                  const g = giorniA(selectedOrg.trialEndsAt);
                  const hasPianoAttivo = selectedOrg.plan && selectedOrg.plan !== "free" && selectedOrg.status === "active";
                  let testo, colore;
                  if (hasPianoAttivo) { testo = `Piano attivo (${selectedOrg.plan})`; colore = "#059669"; }
                  else if (g === null) { testo = "Nessun trial impostato"; colore = "rgba(255,255,255,0.4)"; }
                  else if (g < 0) { testo = `Scaduto da ${Math.abs(g)} giorni`; colore = "#dc2626"; }
                  else { testo = `${g} giorni rimanenti`; colore = g<=14 ? "#d97706" : "#a78bfa"; }
                  return <div style={{fontSize:13,fontWeight:600,color:colore,marginBottom:10}}>{testo}</div>;
                })()}
                <div style={{display:"flex",gap:6}}>
                  <input type="number" min="1" placeholder="giorni" value={giorniProroga}
                    onChange={e=>setGiorniProroga(e.target.value)}
                    style={{width:80,background:"rgba(255,255,255,0.08)",border:"1px solid rgba(255,255,255,0.12)",borderRadius:6,padding:"5px 8px",color:"#fff",fontSize:12,fontFamily:"'Inter',sans-serif"}}/>
                  <button onClick={()=>applicaProroga(selectedOrg)} disabled={applicandoProroga}
                    style={{padding:"5px 12px",border:"none",borderRadius:6,background:"#7c3aed",color:"#fff",fontSize:12,fontWeight:600,cursor:applicandoProroga?"default":"pointer",opacity:applicandoProroga?.6:1,fontFamily:"'Inter',sans-serif"}}>
                    {applicandoProroga ? "..." : "Proroga"}
                  </button>
                  <button onClick={()=>terminaProvaOra(selectedOrg)}
                    title="Imposta la fine della prova a adesso"
                    style={{padding:"5px 10px",border:"1px solid rgba(220,38,38,0.5)",borderRadius:6,background:"transparent",color:"#fca5a5",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                    Termina prova ora
                  </button>
                </div>
              </div>

              {/* Utenti */}
              <div style={{ padding:"14px 20px" }}>
                <div style={{ fontSize:11, fontWeight:600, color:"rgba(255,255,255,0.4)", textTransform:"uppercase", letterSpacing:"0.04em", marginBottom:10 }}>
                  Utenti ({orgUsers.length})
                </div>
                {orgUsers.length === 0 ? (
                  <div style={{ fontSize:13, color:"rgba(255,255,255,0.3)" }}>Nessun utente trovato</div>
                ) : orgUsers.map(u => (
                  <div key={u.uid} style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 0", borderBottom:"1px solid rgba(255,255,255,0.04)" }}>
                    <div style={{ width:28, height:28, borderRadius:"50%", background:"#7c3aed", display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, fontWeight:700, flexShrink:0 }}>
                      {u.displayName?.[0]?.toUpperCase()}
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:13, fontWeight:500 }}>{u.displayName}</div>
                      <div style={{ fontSize:11, color:"rgba(255,255,255,0.3)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{u.email}</div>
                    </div>
                    <span style={{ fontSize:10, padding:"2px 7px", borderRadius:10, background: u.role==="admin"?"rgba(124,58,237,0.3)":"rgba(255,255,255,0.08)", color: u.role==="admin"?"#a78bfa":"rgba(255,255,255,0.5)", fontWeight:600 }}>
                      {u.role}
                    </span>
                    {u.enabled ? <CheckCircle size={13} color="#059669"/> : <XCircle size={13} color="#dc2626"/>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div style={{ background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.1)", borderRadius:12, padding:18, marginTop:20 }}>
          <h3 style={{ fontSize:14, fontWeight:700, color:"#fff", marginBottom:4 }}>🔧 Manutenzione piattaforma</h3>
          <p style={{ fontSize:12, color:"rgba(255,255,255,0.5)", lineHeight:1.6, marginBottom:12 }}>
            <strong style={{color:"rgba(255,255,255,0.75)"}}>Allinea stato commesse</strong> — scrive lo stato «attiva» sulle commesse che non lo hanno.
            Serve una volta sola, dopo un aggiornamento: le pagine ora filtrano le commesse direttamente sul server (per non scaricare tutto lo storico a ogni apertura, che con il tempo costa e rallenta),
            e una commessa senza questo stato non verrebbe restituita dalle query — sparendo dalle tendine.
            Non modifica nulla di già valorizzato: le commesse chiuse restano chiuse. Si può eseguire più volte senza rischi.
          </p>
          <button onClick={allineaStatoCommesse} disabled={allineando}
            style={{ padding:"8px 16px", background: allineando?"rgba(124,58,237,0.4)":"#7c3aed", border:"none", borderRadius:8, color:"#fff", fontSize:13, fontWeight:700, cursor: allineando?"default":"pointer", fontFamily:"'Inter',sans-serif" }}>
            {allineando ? "Allineamento in corso..." : "Allinea stato commesse"}
          </button>
          {esitoAllineamento && (
            <div style={{ marginTop:10, fontSize:12, color: esitoAllineamento.ok ? "#6ee7b7" : "#fca5a5" }}>
              {esitoAllineamento.ok ? "✅" : "⚠️"} {esitoAllineamento.testo}
            </div>
          )}
        </div>
      </div>

      {showNewOrg && <NewOrgModal onClose={() => setShowNewOrg(false)} currentUser={currentUser} onEnterOrg={onEnterOrg}/>}

      {showEliminaOrg && selectedOrg && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:100 }}
          onClick={e => e.target===e.currentTarget && !eliminandoOrg && setShowEliminaOrg(false)}>
          <div style={{ background:"#1e1b4b", border:"1px solid rgba(220,38,38,0.4)", borderRadius:14, padding:24, width:440, maxWidth:"90vw" }}>
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:14, color:"#f87171" }}>
              <AlertTriangle size={20}/>
              <div style={{ fontSize:16, fontWeight:700 }}>Eliminare "{selectedOrg.name}"?</div>
            </div>
            <div style={{ fontSize:13, color:"rgba(255,255,255,0.75)", lineHeight:1.6, marginBottom:16 }}>
              Verranno eliminati <strong>tutti</strong> i dati collegati — clienti, commesse, movimenti economici, dipendenti, documenti, ogni cosa. <strong style={{color:"#f87171"}}>L'operazione non è reversibile.</strong>
              <br/><br/>
              Gli account di accesso (email/password) degli utenti coinvolti restano su Firebase Authentication — vanno rimossi separatamente da lì, se necessario.
            </div>
            <div style={{ fontSize:12, color:"rgba(255,255,255,0.5)", marginBottom:6 }}>
              Per confermare, scrivi il nome esatto dell'organizzazione:
            </div>
            <input value={confermaNomeOrg} onChange={e => setConfermaNomeOrg(e.target.value)}
              placeholder={selectedOrg.name}
              style={{ width:"100%", padding:"9px 12px", borderRadius:8, border:"1px solid rgba(255,255,255,0.15)", background:"rgba(255,255,255,0.05)", color:"#fff", fontSize:13, marginBottom:18, boxSizing:"border-box" }}/>
            <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
              <button onClick={() => setShowEliminaOrg(false)} disabled={eliminandoOrg}
                style={{ padding:"8px 16px", background:"none", border:"1px solid rgba(255,255,255,0.15)", borderRadius:8, color:"rgba(255,255,255,0.7)", fontSize:13, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
                Annulla
              </button>
              <button onClick={eliminaOrganizzazione} disabled={eliminandoOrg || confermaNomeOrg.trim()!==(selectedOrg.name||"").trim()}
                style={{ padding:"8px 16px", background:"#dc2626", border:"none", borderRadius:8, color:"#fff", fontSize:13, fontWeight:700, cursor: eliminandoOrg?"default":"pointer", opacity: confermaNomeOrg.trim()!==(selectedOrg.name||"").trim() ? 0.4 : 1, fontFamily:"'Inter',sans-serif" }}>
                {eliminandoOrg ? "Eliminazione in corso..." : "Elimina definitivamente"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

